/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import { useState, useEffect } from "preact/hooks";

function currentPathname(): string {
  const hash = window.location.hash;
  if (!hash || hash === "#") return "/";
  // Strip query string — wouter matches on path only; query params are read separately
  const path = hash.slice(1).split("?")[0] ?? "/";
  return path.startsWith("/") ? path : "/" + path;
}

export function useHashLocation(): [
  string,
  (to: string, options?: { replace?: boolean }) => void,
] {
  const [loc, setLoc] = useState<string>(currentPathname);

  useEffect(() => {
    const handler = () => setLoc(currentPathname());
    window.addEventListener("hashchange", handler);
    window.addEventListener("popstate", handler);
    return () => {
      window.removeEventListener("hashchange", handler);
      window.removeEventListener("popstate", handler);
    };
  }, []);

  const navigate = (to: string, options?: { replace?: boolean }) => {
    const cleanTo = to.startsWith("#") ? to.substring(1) : to;
    const targetHash = cleanTo.startsWith("/") ? "#" + cleanTo : "#/" + cleanTo;
    if (window.location.hash !== targetHash) {
      if (options?.replace) {
        history.replaceState(null, "", targetHash);
        window.dispatchEvent(new HashChangeEvent("hashchange"));
      } else {
        window.location.hash = targetHash;
      }
    }
  };

  return [loc, navigate];
}
